import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { BsEnvelope, BsPerson } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { getApiUrl } from '../config/api';

const InterestModal = ({ show, handleClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    gender: '',
    ageRange: '',
    interestLevel: 0,
    topics: {
      features: false,
      launchDate: false,
      pricing: false,
      mission: false,
      earlyAccess: false
    }
  });
  const [loading, setLoading] = useState(false);

const API_URL = getApiUrl('INTEREST');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      topics: {
        ...prev.topics,
        [name]: checked
      }
    }));
  };

  const handleAgeRangeSelect = (range) => {
    setFormData(prev => ({
      ...prev,
      ageRange: range
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        if (data.duplicate) {
          toast.info('You have already submitted your interest!');
        } else {
          toast.success('Thank you for your interest!');
        }
        handleClose();
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          gender: '',
          ageRange: '',
          interestLevel: 0,
          topics: {
            features: false,
            launchDate: false,
            pricing: false,
            mission: false,
            earlyAccess: false
          }
        });
      }
    } catch (error) {
      toast.error('❌ Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="md" className="interest-modal">
      <Modal.Header closeButton className="border-0 pb-2">
        <Modal.Title className="modal-title-compact">Nisa Care Launch Interest Form</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3">
        <p className="modal-description">
          Register your interest to receive updates and information about Nisa Care when it launches
        </p>
        <Form onSubmit={handleSubmit}>
          {/* Full Name */}
          <Form.Group className="mb-3">
            <Form.Label className="form-label-compact">Full Name</Form.Label>
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0">
                <BsPerson size={18} />
              </span>
              <Form.Control
                type="text"
                name="fullName"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleInputChange}
                className="border-start-0 ps-0"
                required
              />
            </div>
          </Form.Group>

          {/* Email */}
          <Form.Group className="mb-3">
            <Form.Label className="form-label-compact">Email</Form.Label>
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0">
                <BsEnvelope size={18} />
              </span>
              <Form.Control
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                className="border-start-0 ps-0"
                required
              />
            </div>
          </Form.Group>

          {/* Gender */}
          <Form.Group className="mb-3">
            <Form.Label className="form-label-compact">Gender</Form.Label>
            <Form.Select
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              className="form-select-compact"
              required
            >
              <option value="">Select your gender</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
              <option value="prefer-not-to-say">Prefer not to say</option>
            </Form.Select>
          </Form.Group>

          {/* Age Range */}
          <Form.Group className="mb-3">
            <Form.Label className="form-label-compact">Age Range</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              {['12-19', '20-29', '30-39', '40-59', '60+'].map((range) => (
                <Button
                  key={range}
                  variant={formData.ageRange === range ? 'primary' : 'outline-secondary'}
                  className={`age-btn ${formData.ageRange === range ? 'age-btn-active' : ''}`}
                  onClick={() => handleAgeRangeSelect(range)}
                  type="button"
                  size="sm"
                >
                  {range}
                </Button>
              ))}
            </div>
          </Form.Group>

          {/* Interest Level */}
          <Form.Group className="mb-3">
            <Form.Label className="form-label-compact">How interested are you</Form.Label>
            <div className="d-flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className="star-rating"
                  style={{
                    fontSize: '22px',
                    cursor: 'pointer',
                    color: star <= formData.interestLevel ? '#008080' : '#ddd'
                  }}
                  onClick={() => setFormData(prev => ({ ...prev, interestLevel: star }))}
                >
                  ★
                </span>
              ))}
            </div>
          </Form.Group>

          {/* Topics */}
          <Form.Group className="mb-3">
            <Form.Label className="form-label-compact fw-bold">What topic are you most interested in?</Form.Label>
            <div className="topics-checkboxes">
              <Form.Check
                type="checkbox"
                label="Product features and services"
                name="features"
                checked={formData.topics.features}
                onChange={handleCheckboxChange}
                className="mb-2 form-check-compact"
              />
              <Form.Check
                type="checkbox"
                label="Launch date and availability"
                name="launchDate"
                checked={formData.topics.launchDate}
                onChange={handleCheckboxChange}
                className="mb-2 form-check-compact"
              />
              <Form.Check
                type="checkbox"
                label="Pricing and subscription plans"
                name="pricing"
                checked={formData.topics.pricing}
                onChange={handleCheckboxChange}
                className="mb-2 form-check-compact"
              />
              <Form.Check
                type="checkbox"
                label="Company mission and values"
                name="mission"
                checked={formData.topics.mission}
                onChange={handleCheckboxChange}
                className="mb-2 form-check-compact"
              />
              <Form.Check
                type="checkbox"
                label="Early access opportunities"
                name="earlyAccess"
                checked={formData.topics.earlyAccess}
                onChange={handleCheckboxChange}
                className="form-check-compact"
              />
            </div>
          </Form.Group>

          {/* Submit Button */}
          <div className="d-grid">
            <Button variant="primary" type="submit" className="btn-submit-compact" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Interest'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default InterestModal;